import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { Head } from '../../widgets/Head';
import * as css from './Services.m.css';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Image } from '../../widgets/Image';
import { Section } from '../../widgets/Section';
import { Testimonials } from '../../widgets/Testimonials';
import { Text } from '../../widgets/Text';
import ContentWrapper from '../../widgets/ContentWrapper';
import SummaryList from '../../widgets/SummaryList';
import LinkButton from '../../widgets/LinkButton';
import Page from '../../widgets/Page';
import HeroBackground from '../../widgets/HeroBackground';
import ImageSection from '../../widgets/ImageSection';

const agileIcon = require('../../assets/images/icon-solid/agile.svg');
const prototypeIcon = require('../../assets/images/icon-solid/prototype.svg');
const enterpriseIcon = require('../../assets/images/icon-solid/enterprise.svg');
const fullstackIcon = require('../../assets/images/icon-solid/fullstack.svg');
const performanceIcon = require('../../assets/images/icon-solid/performance.svg');
const qualityIcon = require('../../assets/images/icon-solid/quality.svg');
const devopsIcon = require('../../assets/images/icon-solid/devops.svg');
const approachIcon = require('../../assets/images/icon-solid/approach.svg');
const technologyIcon = require('../../assets/images/icon-solid/technology.svg');
const legacyIcon = require('../../assets/images/icon-solid/legacy.svg');
const deliveryIcon = require('../../assets/images/icon-solid/delivery.svg');
const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const designImg = require('../../assets/images/illustration/design.svg');
const analysisImg = require('../../assets/images/illustration/analysis.svg');
const platformsImg = require('../../assets/images/illustration/app-dev.svg');
const consultingImg = require('../../assets/images/illustration/consulting.svg');
const heroImage = require('../../assets/images/hero-mockups/hero-what-we-do.png');
const heroImageHighRes = require('../../assets/images/hero-mockups/hero-what-we-do@2x.png');

const factory = create();

export const Services = factory(function Services() {
	return (
		<Page>
			{{
				hero: {
					background: <HeroBackground background="level1BlueGreen" />,
					content: (
						<ImageSection
							hero
							reverse
							trailingBias="medium"
							contentBias
							imageGutter="small"
							responsiveMainImageHidden
						>
							{{
								title: (
									<Text type="hero-primary" responsive="callout-head">
										What <br classes={[css.heroBreak]} /> We Do
									</Text>
								),
								blurb: (
									<div classes={css.heroBlurb}>
										<Text type="large-body" responsive="body">
											We help you achieve your web app development{' '}
											<br classes={[css.blurbBreak]} /> goals with confidence
										</Text>
									</div>
								),
								images: {
									main: (
										<Image
											src={heroImage}
											highResSrc={heroImageHighRes}
											width={773}
											height={465}
										/>
									)
								}
							}}
						</ImageSection>
					)
				},
				page: (
					<virtual>
						<Section>
							<ContentWrapper size="smallest" responsive>
								<div classes={css.leading}>
									<div classes={[css.introTitle]}>
										<Text type="subhead" as="h2">
											Developing enterprise web applications requires a lot
											more than an office-full of developers waiting for
											instructions.
										</Text>
									</div>
									<Text type="body">
										You need a complete team that elevates your technical
										expertise and guides significant decisions, keeps
										communication frequent and open, shares knowledge about the
										right tech, tools and standards needed to establish a
										healthy velocity to get your app through to production as
										efficiently as possible.
									</Text>
									<Text type="body">
										Success requires an understanding of every project detail
										without losing focus on big-picture results.
									</Text>
									<Text type="body">
										As leaders in enterprise development services and with our
										position at the forefront of the modern web, we offer a
										partnership that will optimize your approach in all areas of
										development and instill confidence in meeting your company’s
										objectives.
									</Text>
								</div>
							</ContentWrapper>
						</Section>
						<Section shaded>
							<ContentWrapper>
								<SummaryList
									style="alternating-reverse"
									trailingBias="medium"
									listGuttering="none"
									itemPaddingBottom="md"
								>
									{{
										title: (
											<Text type="section" align="center">
												Services We Offer
											</Text>
										),
										items: [
											{
												title: 'Modern Enterprise Web Development',
												blurb: (
													<virtual>
														<div>
															<Text type="body">
																Build and evolve your application
																the right way. Whether we partner
																with your product team or take full
																ownership with our team, we'll bring
																the{' '}
																<Link to="servicesWebApplicationDevelopment">
																	experience and expertise to
																	rapidly develop your application
																</Link>
															</Text>
														</div>
														<div classes={[css.listTitle]}>
															<b>Build the right application with:</b>
														</div>
														<ul classes={[css.list]}>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={agileIcon}
																	alt=""
																	height={24}
																	width={24}
																/>{' '}
																Agile discovery, analysis, and
																planning
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={performanceIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																<Link to="servicesPerformanceOptimization">
																	Performance audits and
																	improvements
																</Link>
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={prototypeIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																<Link to="servicesRapidPrototyping">
																	Rapid prototyping and design
																	cycles
																</Link>
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={qualityIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																Quality assurance testing
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={enterpriseIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																Enterprise UI and UX design
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={devopsIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																<Link to="servicesEnterpriseDevOpsSolutions">
																	DevOps implementation
																</Link>
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={fullstackIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																Full-stack Enterprise JavaScript
															</li>
														</ul>
														<LinkButton to="servicesWebApplicationDevelopment">
															More About Web Development
														</LinkButton>
													</virtual>
												),
												image: (
													<Image
														lazy
														src={platformsImg}
														width={400}
														height={300}
														size="lg"
													/>
												)
											},
											{
												title: 'Strategic & Technical Consulting',
												blurb: (
													<virtual>
														<div>
															<Text type="body">
																Keep up with the evolving web
																engineering landscape and quickly
																adapt to shifting business
																priorities. We help you become{' '}
																<Link to="servicesEnterpriseTechnologyStrategy">
																	strategically positioned
																</Link>{' '}
																by defining, structuring, and
																building the organizational and
																technical capabilities you need for
																lean, rapid web application
																deployment.
															</Text>
														</div>
														<div classes={[css.listTitle]}>
															<b>
																Achieve your strategic goals with:
															</b>
														</div>
														<ul classes={[css.list, css.oneColumnGrid]}>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={approachIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																<span>
																	Development approach, planning,
																	and{' '}
																	<Link to="servicesCrossPlatformDevelopment">
																		implementation
																	</Link>
																</span>
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={technologyIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																<span>
																	Architecture,{' '}
																	<Link to="servicesUXDesign">
																		UX
																	</Link>
																	, open source software, and
																	technology expertise
																</span>
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={legacyIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																<span>
																	<Link to="servicesSoftwareModernization">
																		Legacy application
																	</Link>{' '}
																	maintainability and
																	re-engineering
																</span>
															</li>
															<li classes={css.item}>
																<img
																	loading="lazy"
																	classes={css.img}
																	src={deliveryIcon}
																	alt=""
																	height={24}
																	width={24}
																/>
																Engineering culture, healthy teams,
																and continuous value delivery
															</li>
														</ul>
														<LinkButton to="servicesEnterpriseTechnologyStrategy">
															More About Technical Strategy
														</LinkButton>
													</virtual>
												),
												image: (
													<Image
														lazy
														src={consultingImg}
														width={400}
														height={340}
														size="lg"
													/>
												)
											}
										]
									}}
								</SummaryList>
							</ContentWrapper>
						</Section>
						<Section>
							<ConnectStrip>
								{{
									title: 'Our Expertise Propels Your Success',
									blurb:
										'Accelerate progress toward your goals by leveraging an entire enterprise web development team, instantly.'
								}}
							</ConnectStrip>
						</Section>
						<Section shaded>
							<ContentWrapper size="smaller">
								<SummaryList
									style="alternating"
									trailingBias="medium"
									listGuttering="none"
									itemPaddingBottom="sm"
								>
									{{
										title: (
											<Text type="section" align="center">
												We Will Drive Your Success
											</Text>
										),
										items: [
											{
												title: 'Complete Team',
												blurb: (
													<span>
														Receive trusted guidance from our{' '}
														<Link to="expertise">
															complete cross-functional team
														</Link>{' '}
														on all aspects of agile development:
														Strategy, planning, business analysis,{' '}
														<Link to="servicesEnterpriseArchitecture">
															enterprise architecture
														</Link>
														,{' '}
														<Link to="servicesSoftwareArchitecture">
															software architecture
														</Link>
														, design, development, and{' '}
														<Link to="servicesQualityAssurance">
															quality assurance
														</Link>
														.
													</span>
												),
												image: (
													<Image
														lazy
														src={engineeringImg}
														width={306}
														height={270}
														size="lg"
													/>
												)
											},
											{
												title: 'Holistic Approach',
												blurb: (
													<span>
														Rely on a <a href="/">partner</a> who won’t
														follow blindly but instead considers your
														big-picture goals in every task. We work
														with you to address your entire situation
														and produce exceptional problem-solving
														results.
													</span>
												),
												image: (
													<Image
														lazy
														src={designImg}
														width={306}
														height={333}
														size="lg"
													/>
												)
											},
											{
												title: 'Agile Methodologies',
												blurb: (
													<p>
														Conquer the complexities of building and
														scaling{' '}
														<Link to="servicesEnterpriseSoftwareDevelopment">
															enterprise-level applications
														</Link>{' '}
														with a lean, agile process that effectively
														delivers on all of your requirements.
													</p>
												),
												image: (
													<Image
														lazy
														src={analysisImg}
														width={306}
														height={290}
														size="lg"
													/>
												)
											}
										]
									}}
								</SummaryList>
							</ContentWrapper>
							<div classes={css.testimonials}>
								<ContentWrapper>
									<Testimonials />
								</ContentWrapper>
							</div>
						</Section>
						<Section>
							<ConnectStrip trailing />
						</Section>
					</virtual>
				),
				head: (
					<Head
						title="Custom Web App Development for Enterprises | SitePen"
						description="SitePen provides web app development for businesses and enterprises. We develop new applications and update legacy applications. Contact us to learn more."
					/>
				)
			}}
		</Page>
	);
});

export default Services;
